/* Acquisti — Catalogo Prodotti (per fornitore).
 * Lista filtrabile per fornitore, ricerca, CRUD, import Excel che richiede
 * prima di tutto la selezione del fornitore.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";

const fmtEuro = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AcquistiProdottiPage() {
  const [items, setItems] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [filterForn, setFilterForn] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(null); // {fornitore_pk, mode}
  const fileInputRef = useRef(null);

  const caricaFornitori = async () => {
    try {
      const r = await api.get("/pn/anagrafica", { params: { limit: 5000 } });
      const arr = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      arr.sort((a, b) => (a.ragione_sociale || "").localeCompare(b.ragione_sociale || ""));
      setFornitori(arr);
    } catch (e) { /* ignore */ }
  };

  const carica = async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {};
      if (filterForn) params.fornitore_pk = filterForn;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/acquisti/prodotti", { params });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento prodotti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { caricaFornitori(); }, []);
  useEffect(() => { carica(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterForn]);

  const elimina = async (it) => {
    if (!window.confirm(`Eliminare prodotto "${it.descrizione}"?`)) return;
    try { await api.delete(`/acquisti/prodotti/${it.id}`); await carica(); }
    catch (e) { alert(e.response?.data?.detail || "Errore eliminazione"); }
  };

  // === Flow Import: 1) seleziona fornitore + modalità, 2) carica file ===
  const startImport = () => {
    setImporting({ fornitore_pk: filterForn || "", mode: "append" });
  };

  const procediImport = () => {
    if (!importing?.fornitore_pk) {
      alert("Seleziona il fornitore prima di scegliere il file Excel.");
      return;
    }
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFile = async (file) => {
    if (!file || !importing) return;
    const f = fornitori.find((x) => x.primary_key === importing.fornitore_pk);
    const desc = f ? f.ragione_sociale : importing.fornitore_pk;
    if (!window.confirm(`Importare il file "${file.name}"\nFornitore: ${desc}\nModalità: ${importing.mode === "replace" ? "REPLACE (svuota i prodotti di questo fornitore)" : "APPEND"}\n\nProcedere?`)) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/acquisti/prodotti/import-excel?fornitore_pk=${encodeURIComponent(importing.fornitore_pk)}&mode=${importing.mode}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      const d = r.data || {};
      alert(`Import completato\nFornitore: ${d.fornitore_ragione_sociale || desc}\nInseriti: ${d.inserted}\nScartati: ${d.skipped}`);
      setImporting(null);
      setFilterForn(importing.fornitore_pk);
      await carica();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore import");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="acquisti-prodotti-page" style={{ padding: 16 }}>
      <div style={{ background: "linear-gradient(90deg,#311b92,#5e35b1)", color: "white", padding: "12px 18px", fontSize: 14, fontWeight: 800, letterSpacing: 0.5, borderRadius: 4, marginBottom: 12 }}>
        🛒 CATALOGO PRODOTTI <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.9, marginLeft: 10 }}>(per fornitore)</span>
      </div>

      <div style={{ background: "#f5f5f5", padding: 10, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 4 }}>
        <select value={filterForn} onChange={(e) => setFilterForn(e.target.value)} data-testid="pr-forn"
                style={{ padding: "6px 10px", fontSize: 12, border: "1px solid #bbb", borderRadius: 3, flex: "1 1 260px", maxWidth: 420 }}>
          <option value="">— Tutti i fornitori —</option>
          {fornitori.map((f) => <option key={f.primary_key} value={f.primary_key}>{f.ragione_sociale}</option>)}
        </select>
        <input type="text" placeholder="🔍 Cerca codice / descrizione..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") carica(); }}
               data-testid="pr-search"
               style={{ flex: "1 1 240px", padding: "6px 10px", border: "1px solid #bbb", fontSize: 12, borderRadius: 3 }} />
        <button onClick={carica} disabled={loading} data-testid="pr-refresh"
                style={{ padding: "6px 12px", background: "#5e35b1", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          {loading ? "…" : "🔄 Filtra"}
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#555" }} data-testid="pr-counter"><b>{items.length}</b> prodotti</div>
          <button onClick={() => setEditing({ fornitore_pk: filterForn || "" })} data-testid="pr-new"
                  style={{ padding: "6px 14px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            + Nuovo Prodotto
          </button>
          <button onClick={startImport} data-testid="pr-import-start"
                  style={{ padding: "6px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            📥 Importa Excel
          </button>
        </div>
      </div>

      {err && (<div style={{ background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 12, marginBottom: 8 }}>{err}</div>)}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", background: "white" }}>
          <thead>
            <tr style={{ background: "#311b92", color: "white" }}>
              <th style={{ textAlign: "left", padding: "6px 8px", width: 110 }}>CODICE</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>DESCRIZIONE</th>
              <th style={{ textAlign: "left", padding: "6px 8px", width: 240 }}>FORNITORE</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 60 }}>UM</th>
              <th style={{ textAlign: "right", padding: "6px 8px", width: 90 }}>PREZZO €</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 60 }}>IVA %</th>
              <th style={{ textAlign: "center", padding: "6px 8px", width: 100 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#888" }}>Caricamento…</td></tr>)}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#888", fontStyle: "italic" }}>
                Nessun prodotto. Usa <b>📥 Importa Excel</b> per caricare il catalogo di un fornitore.
              </td></tr>
            )}
            {!loading && items.map((it, i) => (
              <tr key={it.id} data-testid={`pr-row-${it.id}`} style={{ borderTop: "1px solid #eee", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                <td style={{ padding: "5px 8px", fontFamily: "monospace", color: "#311b92", fontWeight: 700 }}>{it.codice || <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ padding: "5px 8px" }}>{it.descrizione}</td>
                <td style={{ padding: "5px 8px", fontSize: 10, color: "#555" }}>{it.fornitore_ragione_sociale || <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace" }}>{it.unita_misura}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>{it.prezzo_unitario > 0 ? `€ ${fmtEuro(it.prezzo_unitario)}` : <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ padding: "5px 8px", textAlign: "center", fontFamily: "monospace" }}>{it.iva_pct ?? <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ padding: "5px 8px", textAlign: "center" }}>
                  <button onClick={() => setEditing(it)} data-testid={`pr-edit-${it.id}`}
                          style={{ padding: "3px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3, borderRadius: 2 }}>✏</button>
                  <button onClick={() => elimina(it)} data-testid={`pr-del-${it.id}`}
                          style={{ padding: "3px 8px", background: "#c62828", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProdottoEditor prodotto={editing} fornitori={fornitori}
                        onClose={() => setEditing(null)}
                        onSaved={async () => { setEditing(null); await carica(); }} />
      )}

      {importing && (
        <ImportProdottiModal
          fornitori={fornitori}
          state={importing}
          setState={setImporting}
          onClose={() => setImporting(null)}
          onProceed={procediImport}
        />
      )}

      <input type="file" ref={fileInputRef} accept=".xlsx,.xls" style={{ display: "none" }}
             onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}

function ImportProdottiModal({ fornitori, state, setState, onClose, onProceed }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="pr-import-modal"
           style={{ background: "white", width: "100%", maxWidth: 520, borderRadius: 6 }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>📥 Import Catalogo Prodotti</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ marginBottom: 14, fontSize: 12, color: "#555", lineHeight: 1.5 }}>
            Seleziona il <b>fornitore</b> e la modalità di importazione, poi clicca <b>Avanti</b> per scegliere il file Excel.
            <br />Il file deve avere come header almeno la colonna <b>Descrizione</b> (e opzionalmente <b>Codice</b>, <b>UM</b>, <b>Prezzo</b>, <b>IVA</b>).
          </div>
          <label style={lbl}>FORNITORE *</label>
          <select value={state.fornitore_pk} onChange={(e) => setState({ ...state, fornitore_pk: e.target.value })}
                  data-testid="pr-import-forn"
                  style={inp}>
            <option value="">— Seleziona fornitore —</option>
            {fornitori.map((f) => <option key={f.primary_key} value={f.primary_key}>{f.ragione_sociale}</option>)}
          </select>
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>MODALITÀ</label>
            <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
              <label data-testid="pr-import-mode-append">
                <input type="radio" name="mode" value="append" checked={state.mode === "append"}
                       onChange={(e) => setState({ ...state, mode: e.target.value })} /> APPEND <span style={{ color: "#888" }}>(aggiunge in coda)</span>
              </label>
              <label data-testid="pr-import-mode-replace">
                <input type="radio" name="mode" value="replace" checked={state.mode === "replace"}
                       onChange={(e) => setState({ ...state, mode: e.target.value })} /> REPLACE <span style={{ color: "#c62828" }}>(svuota prima quelli del fornitore)</span>
              </label>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={onProceed} disabled={!state.fornitore_pk} data-testid="pr-import-proceed"
                  style={{ padding: "6px 16px", background: !state.fornitore_pk ? "#ccc" : "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: !state.fornitore_pk ? "not-allowed" : "pointer", borderRadius: 2 }}>
            Avanti — Scegli file →
          </button>
        </div>
      </div>
    </div>
  );
}

function ProdottoEditor({ prodotto, fornitori, onClose, onSaved }) {
  const isNew = !prodotto.id;
  const [doc, setDoc] = useState({
    codice: prodotto.codice || "",
    descrizione: prodotto.descrizione || "",
    fornitore_pk: prodotto.fornitore_pk || "",
    unita_misura: prodotto.unita_misura || "PZ",
    prezzo_unitario: prodotto.prezzo_unitario ?? 0,
    iva_pct: prodotto.iva_pct ?? "",
    note: prodotto.note || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setDoc((p) => ({ ...p, [k]: v }));

  const salva = async () => {
    if (!doc.descrizione.trim()) { setErr("Descrizione obbligatoria"); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        codice: doc.codice || null,
        descrizione: doc.descrizione,
        fornitore_pk: doc.fornitore_pk || null,
        unita_misura: doc.unita_misura || "PZ",
        prezzo_unitario: Number(doc.prezzo_unitario || 0),
        iva_pct: doc.iva_pct === "" ? null : Number(doc.iva_pct),
        note: doc.note || null,
      };
      if (isNew) await api.post("/acquisti/prodotti", payload);
      else await api.put(`/acquisti/prodotti/${prodotto.id}`, payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="pr-edit-modal"
           style={{ background: "white", width: "100%", maxWidth: 600, maxHeight: "92vh", overflow: "auto", borderRadius: 6 }}>
        <div style={{ background: "#311b92", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>{isNew ? "+ Nuovo Prodotto" : "✏ Modifica Prodotto"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", fontSize: 22, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={lbl}>CODICE</label>
            <input value={doc.codice} onChange={(e) => set("codice", e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>UM</label>
            <input value={doc.unita_misura} onChange={(e) => set("unita_misura", e.target.value.toUpperCase())} style={inp} maxLength={6} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>DESCRIZIONE *</label>
            <input value={doc.descrizione} onChange={(e) => set("descrizione", e.target.value)} style={inp} required />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>FORNITORE</label>
            <select value={doc.fornitore_pk} onChange={(e) => set("fornitore_pk", e.target.value)} style={inp}>
              <option value="">— Nessuno —</option>
              {fornitori.map((f) => <option key={f.primary_key} value={f.primary_key}>{f.ragione_sociale}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>PREZZO UNITARIO €</label>
            <input type="number" step="0.01" value={doc.prezzo_unitario} onChange={(e) => set("prezzo_unitario", e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>IVA %</label>
            <input type="number" step="0.1" value={doc.iva_pct} onChange={(e) => set("iva_pct", e.target.value)} style={inp} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>NOTE</label>
            <textarea rows={2} value={doc.note} onChange={(e) => set("note", e.target.value)} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>
          {err && (<div style={{ gridColumn: "1 / -1", background: "#ffebee", border: "1px solid #ef9a9a", color: "#b71c1c", padding: 8, fontSize: 11, borderRadius: 3 }}>{err}</div>)}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="pr-save"
                  style={{ padding: "6px 16px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: saving ? "wait" : "pointer", borderRadius: 2 }}>
            {saving ? "Salvataggio…" : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 10, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.3, display: "block", marginBottom: 3 };
const inp = { width: "100%", padding: "6px 8px", border: "1px solid #bbb", borderRadius: 3, fontSize: 12, boxSizing: "border-box" };
